import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { Capacitor } from "@capacitor/core";
import {
  getAuth,
  signInWithPopup,
  signOut,
  signInWithCredential,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  indexedDBLocalPersistence,
  initializeAuth,
  linkWithCredential,
  linkWithPopup,
  unlink,
  EmailAuthProvider,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import { app } from "../../config/firebase";
import Logger from "../../utils/logger";

class Auth {
  constructor() {
    this.isNativeMobile = Capacitor.isNativePlatform();

    // Initialize Firebase Auth with proper persistence for mobile platforms
    try {
      if (this.isNativeMobile) {
        this.auth = initializeAuth(app, {
          persistence: indexedDBLocalPersistence,
        });
        Logger.debug(
          "Initialized auth with indexedDBLocalPersistence for mobile"
        );
      } else {
        this.auth = getAuth(app);
        Logger.debug("Initialized auth with default persistence for web");
      }
    } catch (error) {
      // If initializeAuth fails (e.g., already initialized), fall back to getAuth
      Logger.warn(
        "Failed to initialize auth with persistence, falling back to default:",
        error
      );
      this.auth = getAuth(app);
    }

    this.googleProvider = new GoogleAuthProvider();
    this.appleProvider = new OAuthProvider("apple.com");

    Logger.debug(
      "Auth initialized - Platform:",
      this.isNativeMobile ? "mobile" : "web"
    );
  }

  /**
   * Sign in user
   * @returns {Promise<Object>} Firebase user object
   */
  async signIn() {
    try {
      if (this.isNativeMobile) {
        return await this.signInMobile();
      } else {
        return await this.signInWeb();
      }
    } catch (error) {
      Logger.error("Sign in error:", error);
      throw error;
    }
  }

  /**
   * Sign in on mobile platform
   */
  async signInMobile() {
    Logger.debug("Signing in on mobile platform");

    // Step 1: Sign in with Capacitor (native layer)
    const capacitorResult = await FirebaseAuthentication.signInWithGoogle();
    Logger.debug("Capacitor sign in successful");

    // Step 2: Get ID token and sign in to Firebase JS SDK (web layer)
    let idToken;

    // Try to get ID token from the sign in result first
    if (capacitorResult.credential?.idToken) {
      idToken = capacitorResult.credential.idToken;
      Logger.debug("Got ID token from sign in result");
    } else {
      // Get ID token explicitly
      Logger.debug("Getting ID token explicitly");
      const idTokenResult = await FirebaseAuthentication.getIdToken();
      idToken = idTokenResult.token;
    }

    if (!idToken) {
      throw new Error("No ID token available");
    }

    // Sign in to Firebase JS SDK
    const credential = GoogleAuthProvider.credential(idToken);
    const result = await signInWithCredential(this.auth, credential);

    Logger.debug("Firebase JS SDK sign in successful");
    return result;
  }

  /**
   * Sign in on web platform
   */
  async signInWeb() {
    Logger.debug("Signing in on web platform");
    const result = await signInWithPopup(this.auth, this.googleProvider);
    Logger.debug("Web sign in successful");
    return result;
  }

  /**
   * Sign in with email and password.
   * Works on all platforms via the JS SDK (no native OAuth needed).
   * @returns {Promise<Object>} Firebase user credential
   */
  async signInWithEmailPassword(email, password) {
    return await signInWithEmailAndPassword(this.auth, email, password);
  }

  /**
   * Create a new account with email and password.
   * @returns {Promise<Object>} Firebase user credential
   */
  async signUpWithEmailPassword(email, password) {
    return await createUserWithEmailAndPassword(this.auth, email, password);
  }

  /**
   * Send a password reset email to the given address.
   */
  async sendPasswordReset(email) {
    return await sendPasswordResetEmail(this.auth, email);
  }

  /**
   * Whether the current user's email is verified. Google/Apple sign-ins are
   * verified by the provider; password sign-ups start unverified.
   * @returns {boolean}
   */
  get emailVerified() {
    return !!this.auth.currentUser?.emailVerified;
  }

  /**
   * Whether to hold this user at the verify-email screen. Only pure password
   * accounts are gated: social providers (Google/Apple) are trusted identities,
   * and notably Apple sign-ins often report emailVerified=false, so we must NOT
   * gate on that flag alone. A user with any social provider linked is let in.
   * @returns {boolean}
   */
  get needsEmailVerification() {
    const providers = this.linkedProviderIds();
    const passwordOnly =
      providers.length > 0 && providers.every((p) => p === "password");
    return passwordOnly && !this.emailVerified;
  }

  /**
   * Send a verification email to the current user.
   */
  async sendVerificationEmail() {
    const user = this.auth.currentUser;
    if (!user) throw new Error("Not signed in");
    return await sendEmailVerification(user);
  }

  /**
   * Reload the current user from Firebase so emailVerified reflects a link the
   * user just clicked (possibly on another device).
   */
  async reloadUser() {
    await this.auth.currentUser?.reload();
    return this.emailVerified;
  }

  /**
   * Sign in with Apple
   * @returns {Promise<Object>} Firebase user object
   */
  async signInWithApple() {
    try {
      if (this.isNativeMobile) {
        return await this.signInWithAppleMobile();
      } else {
        return await this.signInWithAppleWeb();
      }
    } catch (error) {
      Logger.error("Apple sign in error:", error);
      throw error;
    }
  }

  /**
   * Sign in with Apple on mobile platform
   */
  async signInWithAppleMobile() {
    Logger.debug("Signing in with Apple on mobile platform");

    try {
      // The Capacitor Firebase plugin handles the complete Apple Sign In flow
      const result = await FirebaseAuthentication.signInWithApple({
        skipNativeAuth: true,
      });
      Logger.debug("Capacitor Apple sign in successful", result);

      // Sign in on web layer
      const provider = new OAuthProvider("apple.com");
      const credential = provider.credential({
        idToken: result.credential?.idToken,
        rawNonce: result.credential?.nonce,
      });
      const firebaseResult = await signInWithCredential(this.auth, credential);
      Logger.debug("Firebase JS SDK Apple sign in successful");

      // Return the actual Firebase user with isNewUser info
      return {
        user: firebaseResult.user,
        credential: result.credential,
        additionalUserInfo: firebaseResult.additionalUserInfo || {},
        isNew:
          firebaseResult.user.metadata.creationTime ===
          firebaseResult.user.metadata.lastSignInTime,
      };
    } catch (error) {
      Logger.debug(
        "Apple sign in error, checking if account linking is needed:",
        error
      );

      // If there's an account-exists-with-different-credential error,
      // we need to handle account linking
      if (error.code === "auth/account-exists-with-different-credential") {
        Logger.debug(
          "Account exists with different credential, attempting to link"
        );
        return await this.handleAccountLinking(error);
      }

      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Sign in with Apple on web platform
   */
  async signInWithAppleWeb() {
    Logger.debug("Signing in with Apple on web platform");
    const result = await signInWithPopup(this.auth, this.appleProvider);
    Logger.debug("Web Apple sign in successful");
    return result;
  }

  /**
   * Handle account linking when user already exists with different provider
   */
  async handleAccountLinking(error) {
    try {
      Logger.debug("Handling account linking for existing user");

      // Get the email from the error or from the pending credential
      const email = error.email || error.credential?.email;

      if (!email) {
        throw new Error("No email available for account linking");
      }

      // Check what sign-in methods are available for this email
      const signInMethods = await fetchSignInMethodsForEmail(this.auth, email);
      Logger.debug("Available sign-in methods for email:", signInMethods);

      // If the user has a Google account, sign them in with Google first
      if (signInMethods.includes("google.com")) {
        Logger.debug("User has Google account, signing in with Google first");
        const googleResult = await this.signIn(); // This will use Google sign in

        // Now try to link the Apple credential to the Google account
        if (error.credential) {
          Logger.debug("Linking Apple credential to Google account");
          const linkResult = await linkWithCredential(
            googleResult.user,
            error.credential
          );
          Logger.debug(
            "Successfully linked Apple credential to Google account"
          );
          return linkResult;
        }
      }

      // If we can't link, just use the existing Google account
      Logger.debug("Using existing Google account");
      return await this.signIn();
    } catch (linkError) {
      Logger.error("Account linking failed:", linkError);

      // If linking fails, just sign in with the existing Google account
      Logger.debug("Falling back to Google sign in");
      return await this.signIn();
    }
  }

  /**
   * Sign out user
   */
  async signOut() {
    try {
      if (this.isNativeMobile) {
        await FirebaseAuthentication.signOut();
      }
      await signOut(this.auth); // Both layers do this
      Logger.debug("Signed out from both layers");
    } catch (error) {
      Logger.error("Sign out error:", error);
      throw error;
    }
  }

  /**
   * Get current user
   * @returns {Promise<Object|null>} Current user or null
   */
  async getCurrentUser() {
    try {
      // Wait for auth to be ready
      await new Promise((resolve) => {
        if (this.auth.currentUser === null) {
          const unsubscribe = onAuthStateChanged(this.auth, () => {
            unsubscribe();
            resolve();
          });
        } else {
          resolve();
        }
      });

      if (this.auth.currentUser) {
        Logger.debug("User found in Firebase JS SDK");
        return this.auth.currentUser;
      }

      // If on mobile, check Capacitor and sync if needed
      if (this.isNativeMobile) {
        const capacitorResult = await FirebaseAuthentication.getCurrentUser();
        if (capacitorResult.user) {
          Logger.debug("User found in Capacitor, syncing with Firebase JS SDK");

          try {
            const idTokenResult = await FirebaseAuthentication.getIdToken();
            if (idTokenResult.token) {
              const credential = GoogleAuthProvider.credential(
                idTokenResult.token
              );
              const result = await signInWithCredential(this.auth, credential);
              Logger.debug("Successfully synced with Firebase JS SDK");
              return result.user;
            }
          } catch (error) {
            Logger.warn("Failed to sync with Firebase JS SDK:", error);
          }
        }
      }

      Logger.debug("No user found");
      return null;
    } catch (error) {
      Logger.error("Error getting current user:", error);
      return null;
    }
  }

  /**
   * Listen to auth state changes
   * @param {Function} callback - Callback function to call when auth state changes
   * @returns {Function} Unsubscribe function
   */
  onAuthStateChanged(callback) {
    return onAuthStateChanged(this.auth, callback);
  }

  /**
   * Check if user is signed in
   * @returns {boolean}
   */
  get isSignedIn() {
    return !!this.auth.currentUser;
  }

  /**
   * Current Firebase ID token, for use as the Supabase access token (third-party
   * auth). Firebase's getIdToken() auto-refreshes when the token is near expiry,
   * so this is safe to call per-request. Returns null when signed out.
   * @returns {Promise<string|null>}
   */
  async getIdToken() {
    const user = await this.getCurrentUser();
    return user ? user.getIdToken() : null;
  }

  /**
   * Provider IDs currently linked to the signed-in user.
   * Source of truth is the JS SDK user (all firestore reads use it).
   * @returns {string[]} e.g. ["google.com", "password"]
   */
  linkedProviderIds() {
    return (this.auth.currentUser?.providerData || []).map((p) => p.providerId);
  }

  /**
   * Link an additional sign-in provider to the current user.
   * @param {"google.com"|"apple.com"|"password"} providerId
   * @param {{email?: string, password?: string}} [opts] required for password
   */
  async linkProvider(providerId, opts = {}) {
    const user = this.auth.currentUser;
    if (!user) throw new Error("Not signed in");

    // Idempotent: already linked → nothing to do. The UI's provider list can go
    // stale (JS SDK currentUser lags the native layer), so a "link" tap on an
    // already-linked provider must not blow up with auth/provider-already-linked.
    if (this.linkedProviderIds().includes(providerId)) return user;

    if (providerId === "password") {
      const email = opts.email || user.email;
      if (!email || !opts.password) {
        throw new Error("Email and password required");
      }
      const credential = EmailAuthProvider.credential(email, opts.password);
      return await linkWithCredential(user, credential);
    }

    if (providerId === "google.com") {
      if (this.isNativeMobile) {
        const res = await FirebaseAuthentication.signInWithGoogle({
          skipNativeAuth: true,
        });
        const credential = GoogleAuthProvider.credential(
          res.credential?.idToken
        );
        return await linkWithCredential(user, credential);
      }
      return await linkWithPopup(user, this.googleProvider);
    }

    if (providerId === "apple.com") {
      if (this.isNativeMobile) {
        const res = await FirebaseAuthentication.signInWithApple({
          skipNativeAuth: true,
        });
        const credential = this.appleProvider.credential({
          idToken: res.credential?.idToken,
          rawNonce: res.credential?.nonce,
        });
        return await linkWithCredential(user, credential);
      }
      return await linkWithPopup(user, this.appleProvider);
    }

    throw new Error(`Unsupported provider: ${providerId}`);
  }

  /**
   * Remove a linked sign-in provider from the current user.
   * @param {string} providerId
   */
  async unlinkProvider(providerId) {
    const user = this.auth.currentUser;
    if (!user) throw new Error("Not signed in");
    if (this.linkedProviderIds().length <= 1) {
      throw new Error("You must keep at least one login method.");
    }
    return await unlink(user, providerId);
  }
}

// Export singleton instance
export const authInstance = new Auth();
export default authInstance;
